import type { CategoryContent } from './row.js';

export const LYMPHATIC_NERVOUS: CategoryContent = {
  bscba: [
    [
      'What is the primary function of lymph nodes?',
      [
        'To filter lymph and generate immune responses',
        'To store and regulate blood glucose levels',
        'To produce red blood cells for oxygen transport',
        'To secrete digestive enzymes into the gastrointestinal tract',
      ],
      0,
      'Lymph nodes filter antigens and pathogens from lymph and are sites of B and T cell activation. They are not endocrine or hematopoietic organs.',
    ],
    [
      'Which structure in the lymph node is responsible for bringing lymph into the node?',
      [
        'Afferent lymphatic vessels',
        'Efferent lymphatic vessels',
        'Subcapsular sinus',
        'Medullary cords',
      ],
      0,
      'Afferent lymphatic vessels carry lymph into the node; efferent vessels drain it out. The sinuses are spaces within the node where filtration occurs.',
    ],
    [
      'The central nervous system includes which two organs?',
      [
        'The brain and spinal cord',
        'The brain and cranial nerves',
        'The spinal cord and peripheral ganglia',
        'The cerebellum and autonomic ganglia',
      ],
      0,
      'The CNS comprises the brain and spinal cord. All other neural structures, including nerves and ganglia, are part of the peripheral nervous system.',
    ],
    [
      'Which type of lymphocyte matures in the thymus?',
      [
        'T lymphocytes (T cells)',
        'B lymphocytes (B cells)',
        'Natural killer cells',
        'Memory cells',
      ],
      0,
      'T cells develop in the thymus before migrating to lymphoid organs. B cells mature in the bone marrow; NK cells develop in bone marrow and thymus.',
    ],
    [
      'What is a synapse?',
      [
        'The junction between a neuron and another excitable cell',
        'The region of a neuron that receives electrical signals from other neurons',
        'A type of ion channel found in nerve cell membranes',
        'The protective sheath surrounding axons in the peripheral nervous system',
      ],
      0,
      'A synapse is the structure where presynaptic and postsynaptic cells meet for chemical or electrical transmission. Dendrites receive signals; synapses enable their transfer.',
    ],
    [
      'The thoracic duct drains lymph from which region of the body?',
      [
        'The left side of the body and most abdominal organs',
        'Only the right upper body and right side of the thorax',
        'Only the legs and lower abdomen',
        'Only the head and neck regions',
      ],
      0,
      'The thoracic duct drains the majority of the body. The right lymphatic duct drains only the right upper limb, right thorax, and right head/neck.',
    ],
    [
      'Which of the following is NOT a cranial nerve?',
      [
        'The glossopharyngeal nerve',
        'The vagus nerve',
        'The femoral nerve',
        'The hypoglossal nerve',
      ],
      0,
      'The femoral nerve originates from the lumbar spinal cord (L2-L4), making it a spinal nerve. The glossopharyngeal, vagus, and hypoglossal are cranial nerves (IX, X, XII).',
    ],
    [
      'What do neurotransmitters do at the synapse?',
      [
        'Transmit signals to the postsynaptic cell',
        'Generate action potentials in the axon initial segment',
        'Prevent the reuptake of ions from the synaptic cleft',
        'Strengthen the myelin sheath around axons',
      ],
      0,
      'Neurotransmitters are released by the presynaptic terminal and bind receptors on the postsynaptic cell. They do not generate action potentials or affect myelin.',
    ],
    [
      'Which region of a lymph node is primarily populated by T lymphocytes?',
      [
        'The paracortex (deep cortex)',
        'The follicles and germinal centers',
        'The medullary cords',
        'The subcapsular sinus',
      ],
      0,
      'T cells reside in the paracortex. B cells populate follicles and germinal centers; plasma cells occupy medullary cords; the sinus filters lymph.',
    ],
    [
      'What is the function of the myelin sheath?',
      [
        'To speed up action potential conduction along axons',
        'To prevent the axon from forming synapses',
        'To store glucose for neuron energy metabolism',
        'To synthesize neurotransmitters at the axon terminal',
      ],
      0,
      'Myelin allows saltatory conduction, greatly accelerating action potential speed. It does not synthesize, prevent synapses, or store energy.',
    ],
    [
      'Which of the following best describes the relationship between lymph vessels and blood capillaries?',
      [
        'Lymphatic capillaries return leaked interstitial fluid to the blood',
        'Lymphatic capillaries produce plasma proteins that blood capillaries require',
        'Blood capillaries drain directly into lymphatic vessels without any interstitial space',
        'Lymphatic capillaries transport oxygen and glucose while blood capillaries transport immune cells',
      ],
      0,
      'Lymphatic capillaries recover fluid and proteins that leak from blood capillaries into the interstitium, returning them to the venous system.',
    ],
    [
      'What is the role of the parasympathetic nervous system?',
      [
        'To promote "rest and digest" responses',
        'To increase heart rate and blood pressure in response to threats',
        'To control voluntary muscle contraction',
        'To regulate the production of hormones in the hypothalamus',
      ],
      0,
      'The parasympathetic system promotes rest, digestion, and recovery (via the vagus nerve and sacral outflow). The sympathetic system activates fight-or-flight responses.',
    ],
    [
      'Lymphocyte homing to specific lymphoid organs is primarily directed by which molecules?',
      [
        'Adhesion molecules and chemokines',
        'Hormones secreted by the adrenal gland',
        'Neurotransmitters released by sympathetic nerve endings',
        'Antibodies produced by plasma cells',
      ],
      0,
      'Adhesion molecules (selectins, integrins) and chemokines form a gradient system guiding lymphocyte trafficking. Hormones, neurotransmitters, and antibodies have other roles.',
    ],
    [
      'Which of these structures is part of the peripheral nervous system?',
      [
        'Spinal and cranial nerves',
        'The cerebral cortex',
        'The brainstem',
        'The cerebellum',
      ],
      0,
      'Spinal and cranial nerves are the PNS. The brain structures (cortex, brainstem, cerebellum) are part of the CNS.',
    ],
    [
      'What is lymph primarily composed of?',
      [
        'Filtered plasma with proteins and immune cells',
        'Red blood cells and hemoglobin in suspension',
        'Bile salts and digestive enzymes',
        'Cerebrospinal fluid that has leaked from the brain',
      ],
      0,
      'Lymph is essentially filtered blood plasma plus immune cells. It contains few proteins compared to plasma but many more lymphocytes than blood.',
    ],
  ],
  msc: [
    [
      'How do T cells preferentially accumulate in the paracortex of lymph nodes during an immune response?',
      [
        'Via CCR7 signaling that directs T cells from blood to the paracortex',
        'Through random migration; T cells have no preferential trafficking signal',
        'By being actively transported by dendritic cells that carry them from the cortex into the paracortex',
        'Via CXCL12 gradients that are exclusively found surrounding the germinal center',
      ],
      0,
      'T cells express CCR7 and follow CCL19/CCL21 gradients secreted by fibroblastic reticular cells in the paracortex. This is the primary homing mechanism to this zone.',
    ],
    [
      'Which is the correct anatomical relationship between peyer\'s patches and lymphatic drainage?',
      [
        'Peyer\'s patches are gut-associated lymphoid tissue that drains to mesenteric lymph nodes',
        'Peyer\'s patches are lymph nodes in the mesentery that directly connect to blood vessels',
        'Peyer\'s patches produce lymph directly into the intestinal lumen',
        'Peyer\'s patches have no lymphatic drainage; antigens are absorbed directly into the blood',
      ],
      0,
      'Peyer\'s patches sample intestinal antigens and drain via afferent lymphatics to mesenteric nodes, then via efferent vessels to the thoracic duct.',
    ],
    [
      'What is the structural difference between a multipolar and bipolar neuron?',
      [
        'Multipolar neurons have multiple dendrites; bipolar neurons have one',
        'Multipolar neurons transmit signals faster than bipolar neurons',
        'Bipolar neurons are found only in the spinal cord; multipolar in the brain',
        'Multipolar neurons use acetylcholine; bipolar neurons use dopamine',
      ],
      0,
      'Multipolar neurons have one axon and multiple dendrites (most common); bipolar neurons have one axon and one dendrite (sensory systems, retina).',
    ],
    [
      'How does the blood-brain barrier regulate immune cell entry into the CNS?',
      [
        'Tight junctions restrict transport; adhesion molecules allow selective leukocyte crossing',
        'The blood-brain barrier is fully permeable to all immune cells without regulation',
        'Immune cells never cross into the CNS even in inflammation',
        'All immune cells are freely filtered into the cerebrospinal fluid by the choroid plexus',
      ],
      0,
      'BBB endothelial tight junctions strictly limit paracellular transport. Integrins and selectins on activated leukocytes enable selective transendothelial migration.',
    ],
    [
      'Which neurotransmitter system is primarily responsible for sympathetic nervous system signaling at target organs?',
      [
        'Norepinephrine acting on alpha and beta adrenergic receptors',
        'Acetylcholine acting on nicotinic receptors only',
        'Gamma-aminobutyric acid (GABA) at inhibitory synapses',
        'Serotonin in the dorsal raphe nucleus',
      ],
      0,
      'Sympathetic postganglionic neurons release norepinephrine (except at sweat glands/adrenal medulla where acetylcholine is used). GABA and serotonin have other CNS roles.',
    ],
    [
      'What is the functional significance of High Endothelial Venules (HEVs) in lymph nodes?',
      [
        'Postcapillary venules that enable lymphocyte extravasation',
        'Blood vessels that directly connect to afferent lymphatic vessels',
        'Venues that transport lymph from the medullary cords back to circulation',
        'Vessels responsible for draining the node via the efferent lymphatic vessels',
      ],
      0,
      'HEVs express L-selectin ligands and ICAM-1/VCAM-1; naive lymphocytes exit blood via HEVs through selectin-mediated rolling and integrin binding.',
    ],
    [
      'How does the vagus nerve (CN X) influence immune responses in the gut and other organs?',
      [
        'Parasympathetic fibers release acetylcholine, modulating inflammation',
        'The vagus nerve has no connection to immune function; it only controls digestion',
        'It increases inflammation by releasing TNF-alpha and IL-6',
        'It blocks all lymphocyte activation through sympathomimetic pathways',
      ],
      0,
      'The vagus nerve releases acetylcholine, which via the cholinergic anti-inflammatory pathway dampens macrophage TNF-alpha production and promotes IL-10.',
    ],
    [
      'What is the anatomical basis for the blood-CSF barrier at the choroid plexus?',
      [
        'Tight junctions between choroid plexus epithelial cells',
        'An impermeable wall of bone separating blood from cerebrospinal fluid',
        'Complete mixing of blood and CSF without any barrier function',
        'A layer of arachnoid cells that physically prevent all molecules from crossing',
      ],
      0,
      'The choroid plexus epithelium maintains tight junctions and expresses transporters for selective uptake of nutrients and exclusion of pathogens from CSF.',
    ],
    [
      'Which cranial nerves (CN) carry parasympathetic fibers to the eye?',
      [
        'CN III (oculomotor nerve) innervates the ciliary muscle and pupilary sphincter',
        'CN V (trigeminal nerve) controls all eye movements and pupil responses',
        'CN VI (abducens nerve) innervates the pupil and lens accommodation',
        'CN VII (facial nerve) is the sole source of parasympathetic innervation to the eye',
      ],
      0,
      'CN III parasympathetic preganglionic fibers synapse in the ciliary ganglion; postganglionic fibers innervate the ciliary muscle (accommodation) and sphincter pupillae (constriction).',
    ],
    [
      'What is the route of lymphatic drainage from the upper right limb and right thorax?',
      [
        'Via the right lymphatic duct, which empties into the right subclavian vein',
        'Via the thoracic duct into the left subclavian vein',
        'Directly into the superior vena cava without passing through any ducts',
        'Into the right internal jugular vein and then to the right atrium',
      ],
      0,
      'The right lymphatic duct drains the right upper limb, right thorax, and right head/neck, joining the venous system at the right venous angle.',
    ],
    [
      'What is the microarchitecture of a secondary follicle in a lymph node?',
      [
        'A dark germinal center surrounded by a lighter mantle zone',
        'It is a random aggregation of lymphocytes with no organized zones',
        'It consists of a single layer of epithelial cells',
        'It is located in the medullary region and is devoid of B cells',
      ],
      0,
      'Secondary follicles show germinal centers (GCs) with proliferating centroblasts and somatic hypermutation; the mantle zone surrounds the GC.',
    ],
    [
      'Which spinal cord tract carries pain and temperature sensation from the contralateral side of the body?',
      [
        'The spinothalamic tract, which crosses in the spinal cord',
        'The dorsal column (fasciculus gracilis and cuneatus)',
        'The corticospinal tract (motor, not sensory)',
        'The vestibulocerebellar tract',
      ],
      0,
      'Pain/temperature axons synapse in the dorsal horn, cross in the spinal cord, and ascend as the contralateral spinothalamic tract to the thalamus.',
    ],
    [
      'How does neuroinflammation through microglial activation affect the CNS immune response?',
      [
        'Activated microglia release cytokines and present antigen',
        'Microglia only remove debris and have no role in inflammation',
        'Microglia block all immune responses in the brain permanently',
        'Microglia produce only anti-inflammatory mediators',
      ],
      0,
      'Microglia are CNS resident macrophages that sense danger via PRRs, produce cytokines, and present antigen. They are key to neuroinflammation pathology.',
    ],
    [
      'Which molecules guide B cells specifically to follicles within lymph nodes?',
      [
        'CXCL13-CXCR5 signaling creates a chemotactic gradient toward B-cell zones',
        'CCL19 and CCL21, which also guide T cells',
        'Antigens themselves directly pull B cells into follicles',
        'Fibroblastic reticular cells that physically push B cells into follicles',
      ],
      0,
      'B cells express CXCR5 and follow CXCL13 gradients produced by follicular dendritic cells in germinal centers and follicles.',
    ],
    [
      'What is the structural and functional role of the glomerular filtration barrier (GFB) in immune surveillance of the kidneys?',
      [
        'A highly selective filter blocking large proteins and cells',
        'The GFB is fully permeable to all immune cells for constant kidney patrolling',
        'The GFB has no immune function; it only filters metabolic wastes',
        'The GFB actively transports immune complexes into the urine for removal',
      ],
      0,
      'The GFB (endothelium, basement membrane, podocytes) restricts passage of proteins and cells while permitting immune surveillance. Disruption causes proteinuria.',
    ],
  ],
  phd: [
    [
      'What role does Lymphatic Endothelial Cell (LEC) production of lymphangiogenic factors play in immune trafficking and the resolution of inflammation?',
      [
        'LECs secrete VEGF-C/D to drive lymphangiogenesis during resolution',
        'LECs actively prevent lymphangiogenesis by secreting anti-VEGF molecules',
        'Lymphangiogenesis delays inflammation resolution indefinitely',
        'LECs have no active role in immune trafficking; they are only passive conduits',
      ],
      0,
      'VEGF-C/D-induced lymphangiogenesis during inflammatory resolution increases lymph flow and T cell egress, accelerating clearance of residual antigen and activated lymphocytes.',
    ],
    [
      'How do pattern recognition receptors (PRRs) on tissue-resident immune cells and lymphatic endothelium directly integrate innate sensing with lymphatic trafficking?',
      [
        'TLRs on LECs sense PAMPs, upregulating chemokines and adhesion molecules',
        'PRRs only function in blood; LECs are PRR-negative',
        'PRR signaling suppresses all lymphocyte recruitment to draining nodes',
        'PRRs are found exclusively on T cells, not on stromal or endothelial cells',
      ],
      0,
      'TLR signaling on LECs and antigen-presenting cells induces chemokine and integrin ligand upregulation, amplifying the inflammatory response node.',
    ],
    [
      'How is a dorsal root ganglion (DRG) structurally organized to regulate immune cell access?',
      [
        'DRG somata are protected by satellite glial cells and a fibroblastic capsule',
        'DRGs are immune-privileged and never experience any immune cell infiltration',
        'DRGs lack any organized structure or immune barrier',
        'The DRG capsule is functionally identical to the blood-brain barrier',
      ],
      0,
      'DRG somata are protected by satellite glial cells and a fibroblastic capsule. Immune cell infiltration during neuropathies breaches this barrier via capsular disruption.',
    ],
    [
      'How does the topology of lymphatic capillaries (with their overlapping endothelial junctions and lack of tight junctions) enable selective uptake of lymph-borne antigens and trafficking?',
      [
        'Overlapping junctions let immune cells and antigen enter lymph',
        'Tight junctions in lymphatic capillaries prevent all antigen uptake',
        'Lymphatic capillaries actively pump antigens into themselves against diffusion',
        'Antigens are absorbed only in blood capillaries, not lymphatic capillaries',
      ],
      0,
      'Lymphatic capillary junctions allow interstitial fluid, immune cells, and antigens to enter passively. Macrophages and dendritic cells sample antigens and enter lymph.',
    ],
    [
      'What is the molecular and cellular basis for immune tolerance in the mesenteric lymph nodes (mLNs) to commensal antigens versus activated responses to pathogenic antigens?',
      [
        'Commensal antigens induce tolerogenic DCs that promote regulatory T cells',
        'All antigens from the gut are immediately rejected regardless of source',
        'The mLNs have no capacity to distinguish commensals from pathogens',
        'Commensal antigens trigger the same inflammatory response as pathogenic antigens',
      ],
      0,
      'Tolerogenic DCs (CD103+ CD11b+ fed by TGF-beta-producing epithelial cells) promote iTregs in mLNs. Pathogenic DCs secrete IL-12/IL-23, promoting Th1/Th17 responses.',
    ],
    [
      'How does purinergic signaling (ATP/ADP) through P2X7 receptors on immune cells regulate lymphatic trafficking and inflammation?',
      [
        'ATP activates P2X7, triggering inflammasome activation and IL-1beta release',
        'Purinergic signaling is irrelevant to immune trafficking',
        'P2X7 activation suppresses all immune responses and prevents lymph node activation',
        'ATP only functions in neuronal synaptic transmission, not immune signaling',
      ],
      0,
      'P2X7-mediated IL-1beta amplifies inflammation and enhances leukocyte recruitment to inflamed tissues and their egress into lymphatics toward draining nodes.',
    ],
    [
      'What role do calcitonin gene-related peptide (CGRP) and neuropeptides play in regulating immune cell trafficking through sympathetic lymph-node innervation?',
      [
        'CGRP and substance P from nerve terminals modulate trafficking',
        'Neuropeptides have no role in immune cell trafficking',
        'CGRP exclusively affects pain sensation; it does not influence immunity',
        'Sympathetic innervation of lymph nodes prevents all immune trafficking',
      ],
      0,
      'Sympathetic nerve terminals in lymph nodes release CGRP and NE, modulating vascularity, stromal cell function, and immune cell retention/egress.',
    ],
    [
      'How do follicular helper T cells (Tfh) and follicular dendritic cells (FDCs) coordinate germinal center reactions in lymph nodes, and what lymphatic factors sustain this microenvironment?',
      [
        'FDCs present antigen while VEGF-C expands lymphatics',
        'Tfh and FDCs are not functionally interconnected',
        'Germinal centers require no lymphatic support',
        'Tfh inhibit FDC function and collapse germinal centers',
      ],
      0,
      'Tfh-FDC interaction drives somatic hypermutation and selection; lymphangiogenesis accommodates immune cell expansion and lymphocyte egress from enlarging nodes.',
    ],
    [
      'What is the functional role of circumventricular organs such as the subfornical organ (SFO) and organum vasculosum laminae terminalis (OVLT) in immune sensing?',
      [
        'These organs lack a blood-brain barrier, allowing immune surveillance of the blood',
        'These structures are purely sensory organs for blood osmolarity; they have no immune function',
        'The blood-brain barrier is fully functional at these sites',
        'These organs secrete immunosuppressive factors that block all CNS immune responses',
      ],
      0,
      'CVOs are BBB-lacking sites where tanycytes and resident immune cells sense circulating IL-1, TNF-alpha, and LP, signaling the hypothalamus and dorsal vagal complex.',
    ],
    [
      'How do skin-resident memory T cells (TRM) in the epidermis integrate with lymphatic traffic in the context of a secondary challenge?',
      [
        'TRM persist via IL-15 and recruit innate cells to draining nodes',
        'Skin TRM are immediately flushed into lymphatics upon antigen encounter',
        'TRM provide no protective function and are replaced by circulating memory cells',
        'Lymphatics are absent from skin; TRM function in immune isolation',
      ],
      0,
      'TRM (CD69+ CD103+) provide rapid local control; recruited innate cells disseminate via afferent lymphatics to amplify systemic responses in draining nodes.',
    ],
    [
      'What roles do the periaqueductal gray (PAG) and retrorubral nucleus (RRN) play in nociceptive signaling and immune-pain interactions?',
      [
        'PAG modulates pain gates; RRN mediates analgesia',
        'These structures only control motor movements, not pain or immunity',
        'The PAG has no connection to immune function',
        'Nociceptive pathways bypass the brainstem entirely',
      ],
      0,
      'PAG integrates pain and threat; RRN-mediated descending inhibition gates nociceptive relay. Both engage the HPA axis and sympathetic immune suppression.',
    ],
    [
      'What is the role of medullary thymic epithelial cells (mTECs) and AIRE in central tolerance and lymphatic drainage of apoptotic lymphocytes in the thymus?',
      [
        'AIRE+ mTECs eliminate autoreactive thymocytes via medullary lymphatic clearance',
        'mTECs have no role in tolerance; they only provide structural support',
        'AIRE prevents all T cell maturation and blocks lymphatic drainage',
        'Apoptotic cells accumulate indefinitely in the thymic medulla without drainage',
      ],
      0,
      'AIRE+ mTECs present self-antigens for negative selection. Medullary lymphatics (mlECs expressing CCL21) permit Treg egress and apoptotic cell clearance.',
    ],
    [
      'How do soluble mediators released during neurogenic inflammation (via TRPV1+ sensory neurons) regulate the composition and trafficking of local innate immune cells and their migration into draining lymphatics?',
      [
        'Sensory neuropeptides activate mast cells and drive chemokine-mediated recruitment',
        'Sensory neuropeptides prevent all immune cell recruitment',
        'Neurogenic inflammation is independent of immune cell trafficking',
        'Substance P and CGRP exclusively control pain; they have no immunological role',
      ],
      0,
      'Sensory peptide release drives mast cell degranulation, endothelial permeability, and chemokine production, amplifying local innate inflammation and lymphatic trafficking.',
    ],
    [
      'How does immune memory persist at the level of lymph node stromal architecture and bone-marrow long-lived plasma cell (LLPC) niches?',
      [
        'Bone marrow LLPC niches sustain steady-state antibodies independent of lymphatic recirculation',
        'Immune memory is stored exclusively in blood; lymph nodes are inactive after infection',
        'Bone marrow plasma cells require continuous circulation of antigen via lymphatics to function',
        'Lymph node stromal architecture provides no memory function',
      ],
      0,
      'LLPCs establish bone marrow niches (IL-6, CXCL12 from osteoblasts/CAFs) and persist without active antigen. Memory B cells recirculate, sampling low antigen in FDCs.',
    ],
    [
      'How do Sphingosine-1-phosphate (S1P) gradients across lymph node borders regulate lymphocyte egress versus retention, and what is the role of S1P1 receptor signaling in thymic egress of developing T cells?',
      [
        'S1P1 signaling along a blood-lymphoid gradient drives T cell egress',
        'S1P only functions in barrier lipid metabolism; it has no signaling role in immunity',
        'S1P gradients are identical inside and outside lymph nodes',
        'Thymic egress is controlled exclusively by cortisol and adrenal hormones',
      ],
      0,
      'S1P₁ on egressing T cells senses lower intranodal S1P, driving exit; high extranodal S1P promotes migration toward blood. S1P₁ antagonists (fingolimod) trap lymphocytes clinically.',
    ],
  ],
  professor: [
    [
      'Critically assess the emerging role of lymphatic-derived VEGF-C signaling in the resolution of tertiary lymphoid structures (TLS) in chronic inflammation, autoimmunity, and cancer microenvironments.',
      [
        'VEGF-C lymphangiogenesis aids egress but may enable tumor dissemination',
        'Lymphatic expansion universally suppresses all immune responses',
        'Lymphangiogenesis has no role in TLS biology',
        'VEGF-C only functions in developmental lymphangiogenesis, not homeostatic remodeling',
      ],
      0,
      'TLS regression correlates with lymphangiogenic remodeling and antigen drainage. However, in tumors, TLS-associated lymphangiogenesis may promote escape and systemic dissemination—illustrating context-dependent immune outcomes.',
    ],
    [
      'How do recent single-cell transcriptomics and spatial biology studies reveal previously unrecognized subsets of fibroblastic reticular cells (FRCs) and their specialized roles in segregating T and B cell niches and shaping priming versus tolerance outcomes?',
      [
        'FRC subsets show chemokine profiles that create tolerogenic vs. inflammatory zones',
        'All FRCs are functionally identical',
        'FRCs are only structural; they have no regulatory chemokine roles',
        'Spatial organization of lymph nodes is random and has no bearing on immune outcomes',
      ],
      0,
      'FRC subsets exhibit zone-specific chemokine/TGF-beta signatures (inflammatory CXCL10+ vs. tolerogenic TGF-beta+ FRCs), creating functional domains that direct CD4+ T fate.',
    ],
    [
      'How does the "glymphatic system" overlap with meningeal lymphatic vessels to clear protein aggregates and metabolic byproducts during sleep?',
      [
        'AQP4-dependent glymphatic clearance, aided by meningeal lymphatics',
        'The glymphatic system is not functionally connected to lymphatics',
        'Meningeal lymphatics have no clearance function',
        'Sleep has no bearing on CNS immune clearance',
      ],
      0,
      'Glymphatic-lymphatic dysfunction impairs amyloid-beta clearance during sleep, contributing to accumulation and neuroinflammation in Alzheimer\'s and other proteinopathies.',
    ],
    [
      'How do vagal afferents integrate gut microbiota-immune signals at the nucleus of the solitary tract (NTS) to modulate systemic tolerance and barrier integrity?',
      [
        'Vagal afferents relay microbial signals to the NTS via cholinergic output',
        'The vagus has no connection to the microbiota or immune barrier function',
        'Microbial metabolites directly activate immune cells without CNS integration',
        'The dorsal motor nucleus only controls gastric acid secretion',
      ],
      0,
      'Vagal-gut-immune integration: NTS integrates microbial signals, dorsal motor nucleus activates cholinergic submucosal output, enhancing barrier and anti-inflammatory responses.',
    ],
    [
      'How do recent advances in understanding lymph node lymphatics reveal roles for initial lymphatic endothelial cells (iLECs) in antigen transcytosis and the "local checkpoint" hypothesis in regulating T cell priming?',
      [
        'iLECs transcytose antigen and produce CCL21, gating lymphocyte egress',
        'Antigen transcytosis only occurs in blood capillaries',
        'Initial lymphatics are non-selective; all lymphocytes immediately egress',
        'iLECs have no regulatory role in T cell responses',
      ],
      0,
      'iLECs act as gatekeepers: they transcytose antigen for retained FDCs and DCs, while CCL21-dependent T cell retention prevents premature egress before full priming.',
    ],
    [
      'How does "trained immunity" in tissue-resident innate lymphoid cells (ILCs), via IL-22 and IL-17 cross-talk with lymphatic endothelium, affect barrier-protective immunity and lymphangiogenesis?',
      [
        'ILC3-derived IL-22/IL-17 strengthens epithelium and expands lymphatics',
        'ILCs have no role in barrier function or lymphangiogenesis',
        'IL-22 exclusively promotes inflammatory destruction',
        'Lymphatic expansion is independent of innate lymphoid signaling',
      ],
      0,
      'ILC3-derived IL-22 strengthens epithelial barriers and induces VEGF-C, expanding mucosal lymphatics for enhanced antigen sampling and immune surveillance.',
    ],
    [
      'How do optogenetic and chemogenetic dissections of distinct somatosensory pathways (e.g., C-nociceptors vs. A-delta mechanoreceptors) reveal differential modulation of immune cell trafficking through dorsal horn circuits and rostral spinomesencephalic projections to the PAG?',
      [
        'C-nociceptors drive substance P release; A-delta fibers do not',
        'All sensory modalities produce identical immune effects',
        'Nociceptors have no immune-relevant functions beyond pain signaling',
        'The spinomesencephalic tract only carries pain, not immune-relevant information',
      ],
      0,
      'C-nociceptor signaling triggers dorsal horn IL-6/TNF-alpha release (immune coupling), PAG-mediated analgesia, and sympathetic immune suppression. A-delta fibers do not activate this axis.',
    ],
    [
      'What role does tissue factor (TF) and thrombin signaling on lymphatic endothelial cells play in lymphatic fibrosis and immune dysregulation in long-COVID?',
      [
        'Persistent viral antigens drive TF/PAR signaling and fibrosis',
        'TF signaling is irrelevant to lymphatic pathology',
        'Long-COVID is purely psychological with no immunological basis',
        'Lymphatic fibrosis does not contribute to immune dysregulation',
      ],
      0,
      'TF-thrombin-PAR signaling drives lymphatic ECM remodeling and sustained inflammation in long-COVID, perpetuating T cell exhaustion and autoimmune responses.',
    ],
    [
      'How do circadian lymph flow changes, astrocytic aquaporin-4 (AQP4) glymphatic activity, and sleep-dependent meningeal immune trafficking together clear neuroinflammatory mediators?',
      [
        'AQP4 and meningeal drainage peak in non-REM sleep',
        'Circadian rhythms have no effect on lymphatic function',
        'Meningeal lymphatics are constitutively inactive during sleep',
        'Neuroinflammatory mediators are not cleared via lymphatic pathways',
      ],
      0,
      'Sleep-dependent peaks in glymphatic-lymphatic AQP4 activity and meningeal immune cell egress clear neuroinflammatory cytokines and pathogens, protecting CNS homeostasis.',
    ],
    [
      'How do recent cryo-EM and structural studies of CXCR5 and CCR7 chemokine receptors on B and T cells, combined with cryo-EM of their cognate ligands (CXCL13 and CCL21) bound to glycosaminoglycans (GAGs) in lymph node stromal matrices, explain differential lymphocyte compartmentalization and the molecular basis for "ectopic" lymphoid organ formation in autoimmunity?',
      [
        'GAG-bound chemokine gradients separate B and T cell zones and drive ectopic TLS',
        'Chemokine receptor structure is irrelevant to lymphocyte trafficking',
        'GAGs have no role in chemokine presentation',
        'Ectopic lymphoid structures are randomly distributed with no chemical basis',
      ],
      0,
      'GAG-bound CXCL13/CCL21 create stable gradients in FRC matrices that spatially segregate B/Tfh (CXCR5+) from T cells (CCR7+). Dysregulated GAG-chemokine binding in autoimmunity drives ectopic TLS.',
    ],
    [
      'Critically evaluate the hypothesis that dysregulated acetylcholine synthesis and parasympathetic signaling through the nucleus basalis of Meynert (NBM) and septum impairs cholinergic anti-inflammatory pathways during neuroinflammation in Alzheimer\' disease, Parkinson\' disease, and other neurodegenerative proteinopathies.',
      [
        'NBM cholinergic loss unleashes microglial neuroinflammation',
        'Cholinergic signaling has no role in neuroinflammation',
        'Proteinopathies do not affect cholinergic system integrity',
        'The NBM and septum only regulate memory, not immunity',
      ],
      0,
      'Cholinergic neurodegeneration in Alzheimer\' removes inhibitory ACh signaling on microglia, promoting TNF-alpha and impairing glymphatic clearance of pathogenic amyloid-beta aggregates.',
    ],
    [
      'How do single-cell multi-omic studies integrate transcriptomics, chromatin accessibility (ATAC-seq), and cell-cell interaction predictions to reveal crosstalk between tissue-resident memory T cells, memory B cells, and lymphatic endothelial cells in sustained mucosal immunity and the cellular and molecular basis of trained mucosal immunity?',
      [
        'TRM-derived IL-21 sustains FDC survival and LEC VEGF-C output',
        'TRM, FDC, and LEC crosstalk is not demonstrable at the molecular level',
        'Mucosal memory is short-lived and requires continuous antigen exposure',
        'Lymphatic expansion is antigen-independent and serves no memory function',
      ],
      0,
      'TRM-derived IL-21 sustains FDC BAFF/APRIL production and TGF-beta; LECs expand mucosal lymphatics via VEGF-C, maintaining durable memory B and trained ILC populations.',
    ],
    [
      'How do the enteric nervous system (ENS) and intestinal-associated lymphoid tissue (IALT) communicate bidirectionally via neurotrophic factors and microbial metabolite sensing?',
      [
        'Enteric neurotrophins support ILCs; AhR-vagal signaling curbs macrophage TNF-alpha',
        'The ENS is fully isolated from immune signaling by the epithelial barrier',
        'Enteric neurons only innervate smooth muscle; they have no immune function',
        'Microbial metabolites are absorbed systemically without local mucosal signaling',
      ],
      0,
      'ENS-IALT integration: neurotrophin-ILC signaling + AhR ligand-vagal afferent sensing + cholinergic parasympathetic output create mucosal anti-inflammatory homeostasis.',
    ],
    [
      'What is the emerging understanding of the meningeal immune landscape and the roles of meningeal lymphatics, stromal cells, and border-associated macrophages in CNS immune tolerance versus neuroimmune activation?',
      [
        'Meningeal lymphatics enable antigen drainage while stromal cells and BAMs maintain tolerogenic CNS immunity',
        'The meninges are immune-privileged; no immune cells reside there',
        'Meningeal lymphatics do not contribute to CNS immune tolerance',
        'Border-associated macrophages originate exclusively from circulating monocytes with no developmental pathway from yolk sac macrophages',
      ],
      0,
      'Meningeal LVs + tolerogenic BAM/stromal signaling maintain CNS immune privilege. Loss impairs tolerance and exacerbates CNS autoimmunity and neuroinflammation.',
    ],
    [
      'How do circadian immune oscillations, meningeal lymphatic pulsatility, and sleep-stage-dependent glymphatic CSF-ISF exchange together protect against neurodegeneration?',
      [
        'Circadian egress, meningeal pulsatility, and NREM clearance protect neurons',
        'Circadian rhythms are irrelevant to lymphatic or glymphatic function',
        'Lymphatic pulsatility is unrelated to sleep cycles',
        'Neurodegeneration is unaffected by sleep quality or immune drainage patterns',
      ],
      0,
      'Circadian-immune-glymphatic integration: night peaks in lymphocyte egress + NREM meningeal LV pulsatility + AQP4-mediated CSF clearance maximize neuroprotection.',
    ],
  ],
};
